import { createClient } from '@supabase/supabase-js';
import { setSupabaseClient, supabase as coreSupabase } from '@4cm/core/supabase';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * True when no real Supabase backend is configured — `.env.local` still has
 * the placeholder URL or anon key. Consumers (Brain) use this to drop into
 * an offline path so the UI flow stays demo-able locally without a backend.
 */
export function isPlaceholderSupabase(): boolean {
  if (!url || !anonKey) return true;
  if (url === 'http://localhost:54321') return true;
  if (/placeholder|stub|fake|example/i.test(anonKey)) return true;
  return false;
}

// Hard-fail in production if env vars are missing — better than serving a
// silently-broken app. Dev uses a placeholder that lets the UI render so the
// localStorage onboarding flow can still complete.
if (import.meta.env.PROD) {
  if (!url || !anonKey) {
    throw new Error(
      '[web2] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY in the production build. ' +
      'Set both in your deploy environment before building.',
    );
  }
}

setSupabaseClient(createClient(url ?? 'http://localhost:54321', anonKey ?? 'placeholder'));

export const supabase = coreSupabase;

/**
 * Resolved Supabase URL + anon key. Exported for the rare call site that must
 * hit an Edge Function with a raw `fetch` rather than `functions.invoke` —
 * notably binary audio from the `tts` function: supabase-js only blob-decodes
 * `application/octet-stream`/`application/pdf` responses and text-decodes
 * everything else (incl. `audio/mpeg`), which corrupts the bytes. A raw fetch
 * reading `arrayBuffer()` keeps the audio intact.
 */
export const supabaseUrl = url ?? 'http://localhost:54321';
export const supabaseAnonKey = anonKey ?? 'placeholder';
